import React, { useState, useEffect, useCallback } from 'react';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import Download from 'lucide-react/dist/esm/icons/download';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down';
import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import { useDiscoveryStore } from '../../stores/discoveryStore';
import { discoveryApi } from '../../services/api/discovery';
import { parseMarkdown, formatMessageTime } from '../../utils/markdown';
import type { SpecVersionSummary } from './types';

function filenameFromContentDisposition(header: string | undefined, fallback: string): string {
  if (!header) return fallback;
  const m = header.match(/filename="(.+)"/);
  return m ? m[1] : fallback;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const SpecViewer: React.FC = () => {
  const { activeRequest, setView, setError, loadRequest } = useDiscoveryStore();

  const [versions, setVersions] = useState<SpecVersionSummary[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [specContent, setSpecContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [versionDropdownOpen, setVersionDropdownOpen] = useState(false);

  const requestId = activeRequest?.id;
  const hasSpec = activeRequest?.has_spec || !!activeRequest?.spec_markdown;

  useEffect(() => {
    if (!requestId || !hasSpec) return;

    let cancelled = false;

    const loadVersions = async () => {
      try {
        const { data } = await discoveryApi.listSpecVersions(requestId);
        if (cancelled) return;
        setVersions(data);
        if (data.length > 0) {
          const latest = Math.max(...data.map((v) => v.version));
          setSelectedVersion(latest);
        }
      } catch {
        if (!cancelled) setError('Failed to load spec versions');
      }
    };

    loadVersions();
    return () => { cancelled = true; };
  }, [requestId, hasSpec, setError]);

  useEffect(() => {
    if (!requestId || selectedVersion === null) return;

    let cancelled = false;

    const loadVersion = async () => {
      setLoading(true);
      try {
        const { data } = await discoveryApi.getSpecVersion(requestId, selectedVersion);
        if (cancelled) return;
        setSpecContent(data.spec_markdown);
      } catch {
        if (!cancelled) setError('Failed to load spec version');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadVersion();
    return () => { cancelled = true; };
  }, [requestId, selectedVersion, setError]);

  useEffect(() => {
    if (activeRequest?.spec_markdown && !specContent && versions.length === 0) {
      setSpecContent(activeRequest.spec_markdown);
    }
  }, [activeRequest, specContent, versions]);

  const handleGenerate = useCallback(async () => {
    if (!requestId) return;
    setGenerating(true);
    try {
      await discoveryApi.generateSpec(requestId);
      await loadRequest(requestId);
      const { data } = await discoveryApi.listSpecVersions(requestId);
      setVersions(data);
      if (data.length > 0) {
        const latest = Math.max(...data.map((v) => v.version));
        setSelectedVersion(latest);
      }
    } catch {
      setError('Failed to generate spec');
    } finally {
      setGenerating(false);
    }
  }, [requestId, loadRequest, setError]);

  const handleDownloadMarkdown = useCallback(async () => {
    if (!requestId) return;
    try {
      const res = await discoveryApi.exportSpec(requestId, 'md');
      const cd = res.headers['content-disposition'] ?? res.headers['Content-Disposition'];
      const filename = filenameFromContentDisposition(
        typeof cd === 'string' ? cd : undefined,
        'spec.md',
      );
      triggerBlobDownload(res.data, filename);
    } catch {
      setError('Failed to download specification');
    }
  }, [requestId, setError]);

  const handleExportPDF = useCallback(async () => {
    if (!requestId) return;
    try {
      const res = await discoveryApi.exportSpec(requestId, 'pdf');
      const cd = res.headers['content-disposition'] ?? res.headers['Content-Disposition'];
      const filename = filenameFromContentDisposition(
        typeof cd === 'string' ? cd : undefined,
        'spec.pdf',
      );
      triggerBlobDownload(res.data, filename);
    } catch {
      setError('Failed to export PDF');
    }
  }, [requestId, setError]);

  const handleRequestRevision = useCallback(() => {
    setView('chat');
  }, [setView]);

  const currentVersionInfo = versions.find((v) => v.version === selectedVersion);

  if (!activeRequest) {
    return (
      <div className="flex items-center justify-center h-full text-content-muted text-sm">
        <p>Select a request first.</p>
      </div>
    );
  }

  if (!hasSpec && !specContent) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-content-muted">
        <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-surface-primary border border-subtle mb-4">
          <FileText size={28} className="opacity-30" />
        </div>
        <p className="text-sm mb-1">No specification generated yet.</p>
        <p className="text-xs text-content-faint mb-6 max-w-sm text-center">
          Continue chatting with the Discovery Agent to gather requirements, then generate a spec when ready.
        </p>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setView('chat')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-surface-secondary text-content-secondary text-sm hover:text-content hover:bg-surface-elevated transition-all"
          >
            <ArrowLeft size={16} />
            Back to Chat
          </button>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-brand text-content text-sm font-medium hover:bg-brand/80 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            {generating ? (
              <span className="w-4 h-4 border-2 border-content/30 border-t-content rounded-full animate-spin" />
            ) : (
              <Sparkles size={16} />
            )}
            Generate Spec
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-subtle bg-surface-page">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setView('chat')}
            className="flex items-center justify-center w-8 h-8 rounded-lg text-content-muted hover:text-content hover:bg-surface-secondary transition-all"
          >
            <ArrowLeft size={18} />
          </button>
          <h2 className="text-sm font-semibold text-content">Specification</h2>

          {/* Version dropdown */}
          {versions.length > 0 && (
            <div className="relative">
              <button
                onClick={() => setVersionDropdownOpen(!versionDropdownOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-primary text-xs text-content-secondary hover:text-content hover:bg-surface-secondary transition-all"
              >
                v{selectedVersion}
                {currentVersionInfo && (
                  <span className="text-content-muted ml-1">{formatMessageTime(currentVersionInfo.created_at)}</span>
                )}
                <ChevronDown size={12} className={`transition-transform ${versionDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {versionDropdownOpen && (
                <div className="absolute left-0 top-full mt-1 z-20 bg-surface-page border border-subtle rounded-lg shadow-xl min-w-[200px] max-h-60 overflow-y-auto">
                  {versions
                    .slice()
                    .sort((a, b) => b.version - a.version)
                    .map((v) => (
                      <button
                        key={v.id}
                        onClick={() => {
                          setSelectedVersion(v.version);
                          setVersionDropdownOpen(false);
                        }}
                        className={`flex items-center justify-between w-full px-3 py-2 text-xs transition-all ${
                          v.version === selectedVersion
                            ? 'bg-brand/10 text-primary'
                            : 'text-content-secondary hover:bg-surface-primary'
                        }`}
                      >
                        <span className="font-medium">Version {v.version}</span>
                        <span className="text-content-faint">{formatMessageTime(v.created_at)}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleDownloadMarkdown}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-primary text-xs text-content-tertiary hover:text-content hover:bg-surface-secondary transition-all"
          >
            <Download size={13} />
            Download MD
          </button>
          <button
            onClick={handleExportPDF}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-primary text-xs text-content-tertiary hover:text-content hover:bg-surface-secondary transition-all"
          >
            <Download size={13} />
            Export PDF
          </button>
          <button
            onClick={handleRequestRevision}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand/15 text-xs text-primary font-medium hover:bg-brand/25 transition-all"
          >
            Request Revision
          </button>
        </div>
      </div>

      {/* Spec content */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-8 space-y-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-4 rounded bg-surface-primary animate-pulse" style={{ width: `${60 + Math.random() * 40}%` }} />
            ))}
          </div>
        ) : (
          <div className="max-w-3xl mx-auto px-8 py-8">
            <div
              className="prose-invert text-sm text-content-secondary leading-relaxed [&_h1]:text-xl [&_h1]:font-bold [&_h1]:text-content [&_h1]:mb-4 [&_h1]:mt-8 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-content [&_h2]:mb-3 [&_h2]:mt-6 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-content-secondary [&_h3]:mb-2 [&_h3]:mt-4 [&_h4]:text-sm [&_h4]:font-semibold [&_h4]:text-content-secondary [&_h4]:mb-2 [&_h4]:mt-3 [&_ul]:my-2 [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:pl-5 [&_li]:my-1 [&_code]:bg-black/40 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-primary [&_strong]:text-content [&_em]:text-content-secondary [&_br]:my-2"
              dangerouslySetInnerHTML={{ __html: parseMarkdown(specContent) }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(SpecViewer);
